/**
 * Form state ↔ JSON for Liner webhook mock UI (CloudEvents 1.0 + Maersk org.dcsa.booking.v2).
 */

export interface LinerWebhookMockFormState {
  cloudSpecVersion: string;
  cloudId: string;
  cloudSource: string;
  cloudType: string;
  cloudTime: string;
  cloudDataContentType: string;
  cloudLsaBookingRef: string;
  cloudLinerCode: string;
  cloudBookingStatus: string;
  cloudSubject: string;
  /** Root extension (Maersk / DCSA Booking 2.0). */
  cloudSubscriptionReference: string;

  cloudDataLsaBookingRef: string;
  cloudDataLinerCode: string;
  cloudDataBookingStatus: string;
  cloudDataAmendedBookingStatus: string;
  cloudDataBookingCancellationStatus: string;
  cloudDataCarrierBookingRequestReference: string;
  cloudDataCarrierBookingReference: string;
  cloudDataTransportDocumentReference: string;
  cloudDataCancelStatus: string;
  cloudDataCancelReason: string;
  cloudDataCancelAction: string;
  /** Maersk `data.amendmentStatus`; use literal `(null)` in the field to emit JSON `null`. */
  cloudDataAmendmentStatus: string;
  cloudDataReason: string;
  cloudDataUpdatedDateTime: string;
  cloudDataMetadataJson: string;
  cloudDataFeedbacksJson: string;
  cloudDataBookingJson: string;
  cloudDataAmendedBookingJson: string;
  cloudDataShipmentCutOffTimesJson: string;
}

function newId(): string {
  return crypto.randomUUID();
}

export function defaultLinerWebhookMockFormState(): LinerWebhookMockFormState {
  const iso = new Date().toISOString();
  const lsa = "LSABK-20260321-SAMPLE";
  return {
    cloudSpecVersion: "1.0",
    cloudId: newId(),
    cloudSource: "/maersk/ocean/booking/v2",
    cloudType: "org.dcsa.booking.v2",
    cloudTime: iso,
    cloudDataContentType: "application/json",
    cloudLsaBookingRef: lsa,
    cloudLinerCode: "MAEU",
    cloudBookingStatus: "CONFIRMED",
    cloudSubject: lsa,
    cloudSubscriptionReference: "",

    cloudDataLsaBookingRef: lsa,
    cloudDataLinerCode: "MAEU",
    cloudDataBookingStatus: "CONFIRMED",
    cloudDataAmendedBookingStatus: "",
    cloudDataBookingCancellationStatus: "",
    cloudDataCarrierBookingRequestReference: `BKREQ-ORIG-${lsa}`,
    cloudDataCarrierBookingReference: "MAEU979531000",
    cloudDataTransportDocumentReference: "",
    cloudDataCancelStatus: "",
    cloudDataCancelReason: "",
    cloudDataCancelAction: "",
    cloudDataAmendmentStatus: "",
    cloudDataReason: "",
    cloudDataUpdatedDateTime: "",
    cloudDataMetadataJson: "",
    cloudDataFeedbacksJson: "[]",
    cloudDataBookingJson: JSON.stringify(
      {
        carrierBookingReference: "MAEU979531000",
        carrierBookingRequestReference: `BKREQ-ORIG-${lsa}`,
        bookingStatus: "CONFIRMED",
      },
      null,
      2
    ),
    cloudDataAmendedBookingJson: "",
    cloudDataShipmentCutOffTimesJson: "[]",
  };
}

function isCloudPayload(p: Record<string, unknown>): boolean {
  return (
    typeof p.specversion === "string" &&
    typeof p.type === "string" &&
    p.data != null &&
    typeof p.data === "object"
  );
}

function stringifyJson(v: unknown, fallback: string): string {
  if (v === undefined || v === null) return fallback;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return fallback;
  }
}

export function hydrateFormFromPayload(
  p: Record<string, unknown>
): LinerWebhookMockFormState {
  const base = defaultLinerWebhookMockFormState();
  if (!isCloudPayload(p)) {
    return base;
  }
  const d = p.data as Record<string, unknown>;
  return {
    ...base,
    cloudSpecVersion: String(p.specversion ?? "1.0"),
    cloudId: String(p.id ?? newId()),
    cloudSource: String(p.source ?? ""),
    cloudType: String(p.type ?? ""),
    cloudTime:
      typeof p.time === "string"
        ? p.time
        : p.time instanceof Date
          ? p.time.toISOString()
          : String(p.time ?? ""),
    cloudDataContentType: String(p.datacontenttype ?? "application/json"),
    cloudLsaBookingRef: String(p.lsaBookingRef ?? ""),
    cloudLinerCode: String(p.linerCode ?? ""),
    cloudBookingStatus: String(p.bookingStatus ?? ""),
    cloudSubject: typeof p.subject === "string" ? p.subject : "",
    cloudSubscriptionReference: String(p.subscriptionReference ?? ""),

    cloudDataLsaBookingRef: String(d.lsaBookingRef ?? p.lsaBookingRef ?? ""),
    cloudDataLinerCode: String(d.linerCode ?? p.linerCode ?? ""),
    cloudDataBookingStatus: String(d.bookingStatus ?? ""),
    cloudDataAmendedBookingStatus: String(d.amendedBookingStatus ?? ""),
    cloudDataBookingCancellationStatus: String(d.bookingCancellationStatus ?? ""),
    cloudDataCarrierBookingRequestReference: String(
      d.carrierBookingRequestReference ?? ""
    ),
    cloudDataCarrierBookingReference: String(d.carrierBookingReference ?? ""),
    cloudDataTransportDocumentReference: String(d.transportDocumentReference ?? ""),
    cloudDataCancelStatus: String(d.cancelStatus ?? ""),
    cloudDataCancelReason: String(d.cancelReason ?? ""),
    cloudDataCancelAction: String(d.cancelAction ?? ""),
    cloudDataAmendmentStatus:
      d.amendmentStatus === null ? "(null)" : String(d.amendmentStatus ?? ""),
    cloudDataReason: String(d.reason ?? ""),
    cloudDataUpdatedDateTime: String(d.updatedDateTime ?? ""),
    cloudDataMetadataJson:
      d.metadata !== undefined && d.metadata !== null
        ? stringifyJson(d.metadata, "{}")
        : "",
    cloudDataFeedbacksJson: stringifyJson(d.feedbacks, "[]"),
    cloudDataBookingJson:
      d.booking !== undefined ? stringifyJson(d.booking, "{}") : "",
    cloudDataAmendedBookingJson:
      d.amendedBooking !== undefined ? stringifyJson(d.amendedBooking, "{}") : "",
    cloudDataShipmentCutOffTimesJson: stringifyJson(d.shipmentCutOffTimes, "[]"),
  };
}

function tryParseJson(text: string): { ok: true; value: unknown } | { ok: false; error: string } {
  const t = text.trim();
  if (!t) return { ok: true, value: undefined };
  try {
    return { ok: true, value: JSON.parse(t) as unknown };
  } catch (e) {
    const msg = e instanceof Error ? e.message : "Invalid JSON";
    return { ok: false, error: msg };
  }
}

export type BuildPayloadResult =
  | { ok: true; payload: Record<string, unknown> }
  | { ok: false; error: string };

export function buildPayloadFromForm(s: LinerWebhookMockFormState): BuildPayloadResult {
  const fb = tryParseJson(s.cloudDataFeedbacksJson);
  if (!fb.ok) return { ok: false, error: `data.feedbacks: ${fb.error}` };
  const bk = tryParseJson(s.cloudDataBookingJson);
  if (!bk.ok) return { ok: false, error: `data.booking: ${bk.error}` };
  const ab = tryParseJson(s.cloudDataAmendedBookingJson);
  if (!ab.ok) return { ok: false, error: `data.amendedBooking: ${ab.error}` };
  const sc = tryParseJson(s.cloudDataShipmentCutOffTimesJson);
  if (!sc.ok) return { ok: false, error: `data.shipmentCutOffTimes: ${sc.error}` };

  const data: Record<string, unknown> = {};
  if (s.cloudDataLsaBookingRef.trim()) data.lsaBookingRef = s.cloudDataLsaBookingRef.trim();
  if (s.cloudDataLinerCode.trim()) data.linerCode = s.cloudDataLinerCode.trim();
  if (s.cloudDataBookingStatus.trim()) data.bookingStatus = s.cloudDataBookingStatus.trim();
  if (s.cloudDataAmendedBookingStatus.trim()) {
    data.amendedBookingStatus = s.cloudDataAmendedBookingStatus.trim();
  }
  if (s.cloudDataBookingCancellationStatus.trim()) {
    data.bookingCancellationStatus = s.cloudDataBookingCancellationStatus.trim();
  }
  if (s.cloudDataCarrierBookingRequestReference.trim()) {
    data.carrierBookingRequestReference = s.cloudDataCarrierBookingRequestReference.trim();
  }
  if (s.cloudDataCarrierBookingReference.trim()) {
    data.carrierBookingReference = s.cloudDataCarrierBookingReference.trim();
  }
  if (s.cloudDataTransportDocumentReference.trim()) {
    data.transportDocumentReference = s.cloudDataTransportDocumentReference.trim();
  }
  if (s.cloudDataCancelStatus.trim()) data.cancelStatus = s.cloudDataCancelStatus.trim();
  if (s.cloudDataCancelReason.trim()) data.cancelReason = s.cloudDataCancelReason.trim();
  if (s.cloudDataCancelAction.trim()) data.cancelAction = s.cloudDataCancelAction.trim();

  const amd = s.cloudDataAmendmentStatus.trim();
  if (amd === "(null)") data.amendmentStatus = null;
  else if (amd) data.amendmentStatus = amd;

  if (s.cloudDataReason.trim()) data.reason = s.cloudDataReason.trim();
  if (s.cloudDataUpdatedDateTime.trim()) data.updatedDateTime = s.cloudDataUpdatedDateTime.trim();

  const meta = tryParseJson(s.cloudDataMetadataJson);
  if (!meta.ok) return { ok: false, error: `data.metadata: ${meta.error}` };
  if (
    meta.value !== undefined &&
    typeof meta.value === "object" &&
    meta.value !== null &&
    Object.keys(meta.value as Record<string, unknown>).length > 0
  ) {
    data.metadata = meta.value;
  }

  if (fb.value !== undefined && Array.isArray(fb.value) && fb.value.length > 0) {
    data.feedbacks = fb.value;
  }
  if (bk.value !== undefined && typeof bk.value === "object" && bk.value !== null) {
    data.booking = bk.value;
  }
  if (ab.value !== undefined && typeof ab.value === "object" && ab.value !== null) {
    data.amendedBooking = ab.value;
  }
  if (sc.value !== undefined && Array.isArray(sc.value) && sc.value.length > 0) {
    data.shipmentCutOffTimes = sc.value;
  }

  const out: Record<string, unknown> = {
    specversion: s.cloudSpecVersion.trim() || "1.0",
    id: s.cloudId.trim() || newId(),
    source: s.cloudSource.trim() || "/maersk/ocean/booking/v2",
    type: s.cloudType.trim(),
    time: s.cloudTime.trim() || new Date().toISOString(),
    datacontenttype: s.cloudDataContentType.trim() || "application/json",
    data,
  };
  if (s.cloudLsaBookingRef.trim()) out.lsaBookingRef = s.cloudLsaBookingRef.trim();
  if (s.cloudLinerCode.trim()) out.linerCode = s.cloudLinerCode.trim();
  if (s.cloudBookingStatus.trim()) out.bookingStatus = s.cloudBookingStatus.trim();
  if (s.cloudSubject.trim()) out.subject = s.cloudSubject.trim();
  if (s.cloudSubscriptionReference.trim()) {
    out.subscriptionReference = s.cloudSubscriptionReference.trim();
  }

  return { ok: true, payload: out };
}

/** Copy LSA ref / liner code into root and data slots (quick sync). */
export function applySharedRefs(
  s: LinerWebhookMockFormState,
  lsa: string,
  liner: string
): LinerWebhookMockFormState {
  const orig = lsa.trim() ? `BKREQ-ORIG-${lsa.trim()}` : s.cloudDataCarrierBookingRequestReference;
  return {
    ...s,
    cloudLsaBookingRef: lsa,
    cloudLinerCode: liner,
    cloudSubject: lsa.trim() || s.cloudSubject,
    cloudDataLsaBookingRef: lsa,
    cloudDataLinerCode: liner,
    cloudDataCarrierBookingRequestReference: orig,
  };
}
